import React, { useState, useRef, useEffect } from 'react';
import apiClient from '../api/apiClient';
import { Paperclip, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useSnackbar } from '../contexts/SnackbarContext';
import ClassCardPreview from './common/ClassCardPreview';
import EventCardPreview from './common/EventCardPreview';
import PrayerCardPreview from './common/PrayerCardPreview';

// Component to handle rich text formatting for AI messages
const FormattedMessage = ({ content }) => {
  // Convert text formatting to JSX
  const formatText = (text) => {
    if (!text) return '';
    
    // Split by lines first to handle line breaks
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      if (!line.trim()) {
        // Empty line - render as spacing
        return <br key={lineIndex} />;
      }
      
      // Check for bullet points
      const bulletMatch = line.match(/^[\s]*[•·\-\*]\s*(.+)$/);
      if (bulletMatch) {
        return (
          <div key={lineIndex} className="flex items-start gap-2 my-1">
            <span className="text-blue-600 mt-1">•</span>
            <span className="flex-1">{formatInlineElements(bulletMatch[1])}</span>
          </div>
        );
      }
      
      // Check for numbered lists
      const numberedMatch = line.match(/^[\s]*(\d+\.)\s*(.+)$/);
      if (numberedMatch) {
        return (
          <div key={lineIndex} className="flex items-start gap-2 my-1">
            <span className="text-blue-600 font-medium">{numberedMatch[1]}</span>
            <span className="flex-1">{formatInlineElements(numberedMatch[2])}</span>
          </div>
        );
      }
      
      // Check for headers (lines with ###, ##, #)
      const headerMatch = line.match(/^(#{1,3})\s*(.+)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const HeaderTag = `h${Math.min(level + 2, 6)}`;
        const className = level === 1 ? 'text-lg font-bold mb-2' : 
                         level === 2 ? 'text-base font-semibold mb-1' : 
                         'text-sm font-medium mb-1';
        
        return React.createElement(HeaderTag, {
          key: lineIndex,
          className: className
        }, formatInlineElements(headerMatch[2]));
      }
      
      // Regular paragraph
      return (
        <p key={lineIndex} className="my-1">
          {formatInlineElements(line)}
        </p>
      );
    });
  };
  
  // Handle inline formatting (bold, italic, emojis, etc.)
  const formatInlineElements = (text) => {
    if (!text) return '';
    
    // Handle bold text (**text** or __text__)
    let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // Handle italic text (*text* or _text_)
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Handle code (`text`)
    formatted = formatted.replace(/`(.*?)`/g, '<code class="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono">$1</code>');
    
    // Convert to JSX elements
    return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
  };
  
  return (
    <div className="formatted-message">
      {formatText(content)}
    </div>
  );
};

const AIAssistantNew = ({ groupId, onActionExecuted, isOpen, onClose, groupLogo }) => {
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(null);
  const [loadingState, setLoadingState] = useState(''); // 'creating', 'analyzing', etc.
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  
  // Typewriter effect states
  const [isTyping, setIsTyping] = useState(false);
  const [typingMessage, setTypingMessage] = useState('');
  const [fullAiResponse, setFullAiResponse] = useState('');
  
  // Image upload states
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Snackbar hook for notifications
  const { showAIActionSnackbar } = useSnackbar();
  
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, typingMessage]);  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, isProcessing, typingMessage]);

  // Typewriter effect function
  const typeWriterEffect = (text, callback) => {
    setIsTyping(true);
    setTypingMessage('');
    setFullAiResponse(text);
    
    let currentIndex = 0;
    const typeSpeed = 15; // milliseconds per character (adjust for speed)
    
    const typeNextChar = () => {
      if (currentIndex < text.length) {
        setTypingMessage(text.substring(0, currentIndex + 1));
        currentIndex++;
        
        // Variable speed - faster for spaces, slower for punctuation
        const currentChar = text[currentIndex - 1];
        let delay = typeSpeed;
        
        if (currentChar === ' ') {
          delay = typeSpeed * 0.5; // Faster for spaces
        } else if (['.', '!', '?', '\n'].includes(currentChar)) {
          delay = typeSpeed * 3; // Slower for punctuation and line breaks
        } else if ([',', ';', ':'].includes(currentChar)) {
          delay = typeSpeed * 1.5; // Medium for commas
        }
        
        typingTimeoutRef.current = setTimeout(typeNextChar, delay);
      } else {
        setIsTyping(false);
        setTypingMessage('');
        if (callback) callback();
      }
    };
    
    typeNextChar();
  };

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isProcessing) {
        handleCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, isProcessing]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Image upload handling
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB.');
      return;
    }
    
    setIsUploadingImage(true);
    setError(null);
    
    try {
      // Create image preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        console.log('📸 Frontend: Image preview created successfully');
      };
      reader.readAsDataURL(file);
      
      // Convert to base64 for API
      const base64Reader = new FileReader();
      base64Reader.onload = (e) => {
        const base64Data = e.target.result;
        setUploadedImage(base64Data);
        console.log('📸 Frontend: Base64 conversion successful, length:', base64Data.length);
      };
      base64Reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('📸 Frontend: Image processing error:', error);
      setError('Failed to process image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Helper function to determine preview type from tool name
  const getPreviewType = (toolName) => {
    switch (toolName) {
      case 'create_class':
        return 'class';
      case 'create_event':
        return 'event';
      case 'create_prayer_time':
      case 'update_prayer_time':
      case 'update_prayer_schedule':
        return 'prayer';
      default:
        return 'unknown';
    }
  };

  // Add a message to the chat conversation
  const addChatMessage = (type, content, useTypewriter = false, data = null) => {
    if (type === 'ai' && useTypewriter) {
      // Use typewriter effect for AI messages
      typeWriterEffect(content, () => {
        // After typing is complete, add the message to chat
        const message = {
          id: Date.now(),
          type,
          content,
          timestamp: new Date(),
          ...(data && { data }) // Include additional data if provided
        };
        setChatMessages(prev => [...prev, message]);
      });
    } else {
      // Immediate display for user messages
      const message = {
        id: Date.now(),
        type,
        content,
        timestamp: new Date(),
        ...(data && { data }) // Include additional data if provided
      };
      setChatMessages(prev => [...prev, message]);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;

    setIsProcessing(true);
    setError(null);
    setLoadingState('analyzing');

    try {
      console.log('🤖 AI Agent: Starting request processing...');
      console.log('📝 Input:', input);
      if (uploadedImage) {
        console.log('📸 Image attached, length:', uploadedImage.length);
      }

      const requestData = {
        command: input.trim(),
        groupId,
        ...(uploadedImage && { uploadedImageData: uploadedImage })
      };

      console.log('🚀 AI Agent: Sending request to /ai/command');
      const response = await apiClient.post('/ai/command', requestData);
      console.log('✅ AI Agent: Response received:', response.data);

      if (response.data.success) {
        // Check if this is an immediate execution (old behavior) or preview mode
        if (response.data.phase === 'complete' && response.data.result) {
          // Direct execution - show success and close
          if (onActionExecuted) {
            onActionExecuted(response.data.result);
          }
          
          // Show snackbar notification based on action type
          const actionType = response.data.result?.action || response.data.result?.type;
          if (actionType) {
            if (actionType.toLowerCase().includes('class')) {
              showAIActionSnackbar('class');
            } else if (actionType.toLowerCase().includes('event')) {
              showAIActionSnackbar('event');
            } else if (actionType.toLowerCase().includes('prayer')) {
              showAIActionSnackbar('prayer');
            } else {
              showAIActionSnackbar('action');
            }
          } else {
            showAIActionSnackbar('action');
          }
          
          setTimeout(() => {
            handleCancel();
          }, 1000);
          return;
        }

        // Check if we got tool information for preview
        if (response.data.data && response.data.data.pendingTool) {
          const tool = response.data.data.pendingTool;
          console.log('🔧 Creating preview from tool data:', tool);
          
          // Create preview data structure based on tool
          const previewData = {
            type: getPreviewType(tool.name),
            data: tool.input
          };
          
          setLoadingState('creating');
          
          setTimeout(() => {
            console.log('✅ Setting preview data:', previewData);
            setCurrentPreview(previewData);
            // Add initial user input to chat history
            addChatMessage('user', input);
            // Add initial chat message from AI with typewriter effect and include response data
            const aiMessage = response.data.message || response.data.summary || "I've created this for you! Feel free to ask me to make any changes.";
            addChatMessage('ai', aiMessage, true, response.data);
            setLoadingState('');
            setIsProcessing(false); // Only set this to false after we transition to preview
          }, 200);
        } else {
          // If no tool was used, the AI probably asked questions instead of creating
          // Force it to create something by showing an error that guides the user
          setError(`The AI didn't create anything yet. Please be more specific about what you want to create (e.g., "Create a Torah class on Sundays" or "Set up morning prayers").`);
          setIsProcessing(false);
        }
      } else {
        throw new Error(response.data.message || 'Failed to process request');
      }
    } catch (error) {
      console.error('❌ AI Agent: Request failed:', error);
      setError(error.response?.data?.message || 'Something went wrong. Please try again.');
      setLoadingState('');
      setIsProcessing(false);
    }
  };

  // Handle continuing the conversation in preview mode
  const handleContinueConversation = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isProcessing) return;

    // Add user message to chat
    addChatMessage('user', chatInput.trim());
    const userMessage = chatInput.trim();
    setChatInput('');
    setIsProcessing(true);

    try {
      console.log('🤖 AI Agent: Continuing conversation...');
      console.log('📝 User input:', userMessage);
      console.log('🎭 Current preview context:', currentPreview);

      const requestData = {
        command: userMessage,
        groupId,
        context: 'modify_existing',
        currentPreview: currentPreview,
        conversationHistory: chatMessages.slice(-6), // Include last 6 messages for context
        ...(uploadedImage && { uploadedImageData: uploadedImage })
      };

      console.log('🚀 AI Agent: Sending continuation request to /ai/command');
      const response = await apiClient.post('/ai/command', requestData);
      console.log('✅ AI Agent: Continuation response received:', response.data);

      if (response.data.success) {
        // Add AI response to chat with typewriter effect and include response data
        const aiMessage = response.data.message || response.data.summary || 'I understand. Let me help with that.';
        addChatMessage('ai', aiMessage, true, response.data);

        // Check if the AI made changes to the preview
        if (response.data.data && response.data.data.pendingTool) {
          const tool = response.data.data.pendingTool;
          console.log('🔧 Updating preview from conversation:', tool);
          
          // Update preview data structure
          const updatedPreviewData = {
            type: getPreviewType(tool.name),
            data: tool.input
          };
          
          setCurrentPreview(updatedPreviewData);
        }
      } else {
        throw new Error(response.data.message || 'Failed to process request');
      }
    } catch (error) {
      console.error('❌ AI Agent: Conversation failed:', error);
      addChatMessage('ai', 'Sorry, I had trouble processing that. Could you try rephrasing?', true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle editing fields

  // Handle canceling
  const handleCancel = () => {
    if (isProcessing) return;
    
    setInput('');
    setCurrentPreview(null);
    setUploadedImage(null);
    setImagePreview(null);
    setError(null);
    setLoadingState('');
    setChatMessages([]);
    setChatInput('');
    onClose();
  };

  // Handle applying a suggestion
  const applySuggestion = (suggestion) => {
    setInput(suggestion);
  };

  // Render the preview component based on type
  const renderPreview = () => {
    if (!currentPreview) return null;

    console.log('🎭 Rendering preview for:', currentPreview);
    
    const { type, data } = currentPreview;

    switch (type) {
      case 'class':
        return (
          <ClassCardPreview
            classData={data}
            groupLogo={groupLogo}
            mode="preview"
          />
        );
      case 'event':
        return (
          <EventCardPreview
            eventData={data}
            groupLogo={groupLogo}
            mode="preview"
          />
        );
      case 'prayer':
        return (
          <PrayerCardPreview
            prayerData={data}
            mode="preview"
          />
        );
      default:
        console.warn('🚨 Unknown preview type:', type);
        return (
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">Preview not available for type: {type}</p>
            <pre className="text-xs text-gray-500 mt-2 overflow-auto">
              {JSON.stringify(currentPreview, null, 2)}
            </pre>
          </div>
        );
    }
  };

  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={!isProcessing ? handleCancel : undefined}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2">
        <div 
          className={`w-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden relative ${
            currentPreview ? 'max-w-7xl' : 'max-w-2xl'
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{ 
            height: currentPreview ? '95vh' : 'auto',
            maxHeight: '95vh',
            minHeight: currentPreview ? '85vh' : 'auto'
          }}
        >
          {/* Close button - always visible */}
          <button
            onClick={!isProcessing ? handleCancel : undefined}
            className="absolute top-4 right-4 z-20 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isProcessing}
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {!currentPreview ? (
            /* Input Mode */
            <div className="p-8 flex flex-col items-center justify-center min-h-[400px]">
              {/* Logo and Title */}
              <div className="text-center mb-6">
                <div 
                  className="w-20 h-20 mx-auto mb-3 flex items-center justify-center"
                >
                  <img 
                    src="/yesod-logo.png" 
                    alt="Yesod AI" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  What would you like me to create?
                </h1>
                <p className="text-gray-600">
                  I can create classes, events, or prayer times. Just describe it in plain language.
                </p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              {/* Main Input Form */}
              <form onSubmit={handleSubmit} className="w-full">
                <div className="relative">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (input.trim() && !isProcessing) {
                          handleSubmit(e);
                        }
                      }
                    }}
                    placeholder="Create a Torah class every Sunday at 7pm with Rabbi Cohen..."
                    className="w-full h-32 p-4 border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:border-blue-500 transition-colors text-lg"
                    disabled={isProcessing}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!input.trim() || isProcessing}
                  className="w-full mt-4 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>
                        {loadingState === 'analyzing' ? 'Analyzing...' : 
                         loadingState === 'creating' ? 'Creating...' : 'Processing...'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <img 
                        src="/assets/RocketIcon.png" 
                        alt="Create" 
                        className="w-5 h-5"
                      />
                      <span>Create with Yesod AI</span>
                    </div>
                  )}
                </button>
                
                {/* Beta Consent */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-gray-600 text-center leading-relaxed">
                    Yesod AI is in beta and may occasionally produce errors or unexpected results. By proceeding, you acknowledge these limitations and consent to our{' '}
                    <a 
                      href="/#/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </form>
            </div>
          ) : (
            /* Preview Mode with Chat - Side by Side Layout */
            <div className="flex h-full">
              {/* Left Side - Preview */}
              <div className="w-1/3 flex flex-col border-r border-gray-200 bg-gray-50">
                <div className="flex-shrink-0 py-3 px-4 border-b border-gray-200 bg-white flex items-center min-h-[60px]">
                  <h2 className="text-sm font-medium text-gray-900">
                    {currentPreview.type === 'class' ? 'Class' : 
                     currentPreview.type === 'event' ? 'Event' : 'Prayer Time'} Preview
                  </h2>
                </div>
                
                {/* Preview Content - Full height container */}
                <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
                  <div className="transform scale-110 origin-center max-w-full">
                    {renderPreview()}
                  </div>
                </div>
              </div>

              {/* Right Side - Chat Conversation Area */}
              <div className="w-2/3 flex flex-col min-h-0 overflow-hidden">
                {/* Chat Header */}
                <div className="flex-shrink-0 py-3 px-4 border-b border-gray-200 bg-white flex flex-col justify-center min-h-[60px]">
                  <h2 className="text-sm font-medium text-gray-900">Chat</h2>
                  <p className="text-xs text-gray-500">Ask me to make changes or save when ready</p>
                </div>
                
                {/* Chat Messages - Scrollable */}
                <div 
                  ref={chatContainerRef}
                  className="flex-1 overflow-y-auto p-4 space-y-4 bg-white"
                >
                  {chatMessages.map((message) => (
                    <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {message.type === 'ai' && (
                        <div className="flex-shrink-0 mr-2 mt-1">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200">
                            <img 
                              src="/yesod-logo.png" 
                              alt="Yesod AI" 
                              className="w-5 h-5 object-contain"
                            />
                          </div>
                        </div>
                      )}
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.type === 'user' 
                          ? 'bg-gray-100 text-gray-800 rounded-br-none' 
                          : 'text-gray-800 rounded-bl-none'
                      }`}>
                        <div className="text-sm">
                          {message.type === 'user' ? (
                            <p>{message.content}</p>
                          ) : (
                            <FormattedMessage content={message.content} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing animation for AI response */}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="flex-shrink-0 mr-2 mt-1">
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200">
                          <img 
                            src="/yesod-logo.png" 
                            alt="Yesod AI" 
                            className="w-5 h-5 object-contain"
                          />
                        </div>
                      </div>
                      <div className="text-gray-800 rounded-lg rounded-bl-none px-4 py-2 max-w-xs lg:max-w-md">
                        <div className="text-sm">
                          <FormattedMessage content={typingMessage} />
                          <span className="inline-block w-2 h-4 bg-gray-800 ml-1 animate-pulse"></span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Loading indicator in chat */}
                  {isProcessing && !isTyping && (
                    <div className="flex justify-start">
                      <div className="flex-shrink-0 mr-2 mt-1">
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200">
                          <img 
                            src="/yesod-logo.png" 
                            alt="Yesod AI" 
                            className="w-5 h-5 object-contain"
                          />
                        </div>
                      </div>
                      <div className="text-gray-800 rounded-lg rounded-bl-none px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm">Working...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Error Display */}
                {error && (
                  <div className="flex-shrink-0 p-3 mx-4 mb-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                )}

                {/* Chat Input - Always fixed at bottom */}
                <div className="flex-shrink-0 p-4 border-t-2 border-gray-200 bg-white shadow-lg">
                  <form onSubmit={handleContinueConversation} className="flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Ask me to change something or add 'save it' when ready..."
                        className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isProcessing || isTyping}
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || isProcessing || isTyping}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      Send
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AIAssistantNew;
